import { useCallback } from 'react';
import type { FormInstance } from 'antd';

import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import type { PPGRow } from '../../../../types/promoMetrics';
import type { ApiPPG } from '../../../../types/promoMetrics';
import { buildPromoPayload } from '../../../../utils/buildPromoPayload';
import { buildChangesPayload } from '../../utils/promoStateUtils';
import type { CreatePromoPayload } from '../mutations/useCreatePromoMutation';

interface UsePromoSaveArgs {
    promoForm: FormInstance;
    ppgData: PPGRow[];
    baseline: Map<string, Record<string, number>>;
    planId: string;
    eventId: string;
    savedPromotionId: string | null;
    promoMeta: { promotion_id: string } | null;
    existingPromoFormValues: Record<string, unknown> | null;
    existingPpgData: ApiPPG[];
    isEditMode: boolean;
    createPromo: (payload: CreatePromoPayload) => void;
    resetPpgToExisting: (existingRows: ApiPPG[]) => void;
    clearPpgState: () => void;
    triggerUpliftBeforeSave: () => Promise<boolean>;
}

interface UsePromoSaveReturn {
    save: () => Promise<void>;
    reset: () => void;
}

const usePromoSave = ({
    promoForm,
    ppgData,
    baseline,
    planId,
    eventId,
    savedPromotionId,
    promoMeta,
    existingPromoFormValues,
    existingPpgData,
    isEditMode,
    createPromo,
    resetPpgToExisting,
    clearPpgState,
    triggerUpliftBeforeSave,
}: UsePromoSaveArgs): UsePromoSaveReturn => {
    const { promoFields, entityAttributes } = useTpoGenConfig();

    const save = useCallback(async () => {
        try {
            await promoForm.validateFields();
        } catch {
            return;
        }

        // Pre-save uplift: fetch latest uplift values before persisting
        const canProceed = await triggerUpliftBeforeSave();
        if (!canProceed) return;

        const fv = promoForm.getFieldsValue(true);
        const fieldsPayload = buildPromoPayload(entityAttributes, promoFields, fv);
        const changes = buildChangesPayload(ppgData, baseline);

        createPromo({
            plan_id: planId,
            event_id: eventId,
            promotion_id: savedPromotionId ?? promoMeta?.promotion_id ?? null,
            ...fieldsPayload,
            ppg_ids: ppgData.map((p) => p.ppg_id),
            changes,
            user_id: localStorage.getItem('email') ?? '',
        } as CreatePromoPayload);
    }, [
        promoForm,
        ppgData,
        baseline,
        planId,
        eventId,
        createPromo,
        savedPromotionId,
        promoMeta,
        triggerUpliftBeforeSave,
        promoFields,
        entityAttributes,
    ]);

    const reset = useCallback(() => {
        if (isEditMode && existingPromoFormValues) {
            promoForm.resetFields();
            promoForm.setFieldsValue(existingPromoFormValues);
            resetPpgToExisting(existingPpgData);
        } else {
            promoForm.resetFields();
            clearPpgState();
        }
    }, [isEditMode, existingPromoFormValues, existingPpgData, promoForm, resetPpgToExisting, clearPpgState]);

    return { save, reset };
};

export default usePromoSave;
